import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useForm, Controller, FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { UserRole, UserRoleLabels, CreateUserData, UpdateUserData } from '@/types/user';
import { UserCompany } from '@/services/users/users.service';

type FormMode = 'create' | 'edit';

// Base schema for common fields
const baseSchema = {
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
  fullName: z
    .string()
    .min(2, 'Nome deve ter no mínimo 2 caracteres')
    .max(255, 'Nome deve ter no máximo 255 caracteres'),
  role: z.nativeEnum(UserRole),
};

// Password validation for create mode
const passwordSchema = z
  .string()
  .min(12, 'Senha deve ter no mínimo 12 caracteres')
  .max(128, 'Senha deve ter no máximo 128 caracteres')
  .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
  .regex(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
  .regex(/[0-9]/, 'Senha deve conter pelo menos um número')
  .regex(/[^A-Za-z0-9]/, 'Senha deve conter pelo menos um caractere especial');

// Create mode schema - password is required
const createUserSchema = z.object({
  ...baseSchema,
  password: passwordSchema,
});

// Edit mode schema - password is optional but must meet requirements if provided
const editUserSchema = z.object({
  ...baseSchema,
  password: z.string().superRefine((val, ctx) => {
    // If empty, it's valid (no password change)
    if (val.length === 0) return;

    // If not empty, must meet all requirements
    if (val.length < 12) {
      ctx.addIssue({
        code: 'custom',
        message: 'Senha deve ter no mínimo 12 caracteres',
      });
    }
    if (val.length > 128) {
      ctx.addIssue({
        code: 'custom',
        message: 'Senha deve ter no máximo 128 caracteres',
      });
    }
    if (!/[A-Z]/.test(val)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Senha deve conter pelo menos uma letra maiúscula',
      });
    }
    if (!/[a-z]/.test(val)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Senha deve conter pelo menos uma letra minúscula',
      });
    }
    if (!/[0-9]/.test(val)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Senha deve conter pelo menos um número',
      });
    }
    if (!/[^A-Za-z0-9]/.test(val)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Senha deve conter pelo menos um caractere especial',
      });
    }
  }),
});

type UserFormData = z.infer<typeof createUserSchema>;

interface UserFormDialogProps {
  open: boolean;
  mode: FormMode;
  user?: UserCompany;
  onClose: () => void;
  onSubmit: (data: CreateUserData | UpdateUserData) => Promise<void>;
}

/**
 * Unified dialog for creating and editing users
 *
 * Features:
 * - Create mode: All fields required including password
 * - Edit mode: Password optional (only if changing), pre-filled with user data
 * - Form validation with Zod
 * - Password visibility toggle
 * - Role selection (operations or admin)
 * - Loading states
 * - Error handling
 */
export const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  mode,
  user,
  onClose,
  onSubmit,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEditMode = mode === 'edit';
  const schema = isEditMode ? editUserSchema : createUserSchema;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: async (data) => {
      try {
        const validatedData = schema.parse(data);
        return {
          values: validatedData,
          errors: {},
        };
      } catch (error) {
        if (error instanceof z.ZodError) {
          const fieldErrors: FieldErrors<UserFormData> = {};
          error.issues.forEach((issue) => {
            const fieldName = issue.path[0] as keyof UserFormData;
            if (fieldName && !fieldErrors[fieldName]) {
              fieldErrors[fieldName] = {
                type: issue.code,
                message: issue.message,
              };
            }
          });
          return {
            values: {},
            errors: fieldErrors,
          };
        }
        return {
          values: {},
          errors: {},
        };
      }
    },
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
      fullName: '',
      role: UserRole.OPERATIONS,
    },
  });

  // Reset form when dialog opens or user changes
  useEffect(() => {
    if (open) {
      if (isEditMode && user) {
        // Pre-fill with user data in edit mode
        const roleValue = user.role === 'operation' ? UserRole.OPERATIONS : UserRole.ADMIN;
        reset({
          email: user.email,
          fullName: user.fullName,
          role: roleValue,
          password: '', // Empty password means no change
        });
      } else {
        // Reset to defaults in create mode
        reset({
          email: '',
          password: '',
          fullName: '',
          role: UserRole.OPERATIONS,
        });
      }
      setSubmitError(null);
      setShowPassword(false);
    }
  }, [open, isEditMode, user, reset]);

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setSubmitError(null);
      setShowPassword(false);
      onClose();
    }
  };

  const handleFormSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isEditMode) {
        // In edit mode, only send fields that changed
        const updateData: UpdateUserData = {
          email: data.email,
          fullName: data.fullName,
          role: data.role,
        };
        // Only include password if it was provided
        if (data.password && data.password.length > 0) {
          updateData.password = data.password;
        }
        await onSubmit(updateData);
      } else {
        // In create mode, send all required fields
        await onSubmit(data as CreateUserData);
      }
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError(
          isEditMode ? 'Erro ao atualizar usuário. Tente novamente.' : 'Erro ao criar usuário. Tente novamente.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    return isEditMode ? 'Editar Usuário' : 'Novo Usuário';
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) {
      return isEditMode ? 'Salvando...' : 'Criando...';
    }
    return isEditMode ? 'Salvar Alterações' : 'Criar Usuário';
  };

  const getPasswordHelperText = () => {
    if (errors.password?.message) {
      return errors.password.message;
    }
    if (isEditMode) {
      return 'Deixe em branco para manter a senha atual';
    }
    return 'Mínimo 12 caracteres, incluindo maiúscula, minúscula, número e caractere especial';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          {getTitle()}
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Full Name Field */}
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome Completo"
                  fullWidth
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  disabled={isSubmitting}
                  autoFocus
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            {/* Email Field */}
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting || isEditMode} // Disable email in edit mode
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            {/* Password Field */}
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={isEditMode ? 'Nova Senha (opcional)' : 'Senha'}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  error={!!errors.password}
                  helperText={getPasswordHelperText()}
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isSubmitting}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            {/* Role Field */}
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Cargo"
                  select
                  fullWidth
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AdminIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                >
                  {Object.values(UserRole).map((role) => (
                    <MenuItem key={role} value={role}>
                      {UserRoleLabels[role]}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            variant="outlined"
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {getSubmitButtonText()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
